import { i18n } from "./i18nService";
import { isSuitableFor4KPlayback } from "./titleDataService";
import { getProgressData } from "./mediaListService";

/**
 * Maps available subtitle languages.
 * @param {Object} obj - Subtitle languages key-value object.
 * @returns {Array} Array of title's available subtitle languages.
 * @private
 */
function mapSubtitles(obj) {
  return Object.keys(obj).map(lang => i18n.get(`global.subtitleLanguages.${lang}`) || lang);
}

/**
 * Gets movie duration.
 * @param {Number} duration - Movie duration.
 * @returns {String} Movie duration string label.
 * @private
 */
function getMovieDuration(duration) {
  const MINUTES_SHORT = i18n.get("pages.detail.mins");
  return `${Math.floor(duration / 60)} ${MINUTES_SHORT}`;
}

/**
 * Calculates elapsed time for movie or last seen episode.
 * @param {Number} videoDuration - Movie/episode video total duration.
 * @param {Number} playbackTime - Playback time.
 * @returns {String} Title remaining time label.
 * @private
 */
function calculateRemainingTime(videoDuration, playbackTime) {
  const HOURS = i18n.get("pages.detail.hours");
  const MINUTES_SHORT = i18n.get("pages.detail.mins");
  const REMAINING_TIME = i18n.get("pages.detail.left");

  const elapsedTime = videoDuration - playbackTime;
  const elapsedMinutes = Math.floor(elapsedTime / 60);

  if (elapsedMinutes > 60) {
    const hours = elapsedMinutes / 60;
    const rhours = Math.floor(hours);
    const minutes = (hours - rhours) * 60;
    const rminutes = Math.round(minutes);
    return `(${rhours} ${HOURS} ${rminutes} ${MINUTES_SHORT} ${REMAINING_TIME})`;
  }
  return `(${elapsedMinutes} ${MINUTES_SHORT} ${REMAINING_TIME})`;
}

/**
 * Formats data to be shown on detail page. Needs a React component class context since
 * this method is making use of this.state and other inner context variables (this.isSeries, this.progres...).
 * @param {Object} data - Title data object
 * @param {String} seasons - Seasons duration text, in case title is a series.
 * @returns {Object} Title data to be displayed on page.
 * @public
 */
export function formatDetailData(data, seasons) {
  return {
    title: data.title,
    year: data.year,
    shortDescription: data.shortDescription,
    duration: !this.isSeries ? getMovieDuration(this.mediaData.videoHD.duration) : seasons,
    genres: (data.tagList.genres && data.tagList.genres.slice(0, 2)) || [],
    ageRating: data.ageRating,
    languages: data.languages.map(lang => i18n.get(`global.languages.${lang}`) || lang),
    hasFrenchAudio: getHasFrenchAudio(data),
    subtitles:
      // FIXME: Find out where the subtitles information comes from when on a series detail page. Now it's taken from the first episode.
      data.mediaContent[0].content.subtitles && mapSubtitles(data.mediaContent[0].content.subtitles),
    has4K: isSuitableFor4KPlayback(this.state.has4K),
    countryOfOrigin: data.countryOfOrigin,
    cast: data.credits && data.credits.actors,
    creator: data.credits && data.credits.creators,
    director: data.credits && data.credits.directors,
    image: data.images.poster,
    trailer: !!this.state.trailer,
    progress: this.progress && this.progress.percentage * 100,
    remainingTime: this.progress && calculateRemainingTime(this.mediaData.videoHD.duration, getProgressData.call(this)),
    resumeEpisode: this.resumeEpisode,
    isLive: data.isLive,
    labels: {
      language:
        data.languages && data.languages.length === 1
          ? i18n.get("global.language")
          : i18n.get("pages.detail.languages"),
      subtitles: i18n.get("global.subtitles"),
      director:
        data.credits && data.credits.directors && data.credits.directors.length === 1
          ? i18n.get("pages.detail.director")
          : i18n.get("pages.detail.directors"),
      creator:
        data.credits && data.credits.creators && data.credits.creators.length === 1
          ? i18n.get("pages.detail.creator")
          : i18n.get("pages.detail.creators"),
      countryOfOrigin: i18n.get("pages.detail.countryOfOrigin"),
      cast: i18n.get("pages.detail.cast"),
      resume: i18n.get("pages.detail.resume"),
      play: i18n.get("pages.detail.play"),
      playFromBeginning: i18n.get("global.playFromBeginning"),
      trailer: i18n.get("pages.detail.trailer"),
      myWatchlist: i18n.get("pages.myLibrary.myWatchlist")
    }
  };
}

/**
 * Gets a label of episode available (last seen of first one available in episodes list).
 * @param {Number} seasonNumber - Season number.
 * @param {Number} episodeNumber - Episode number.
 * @returns {String} Season-episode label.
 * @public
 */
export function getEpisodeLabel(seasonNumber = 1, episodeNumber = 1) {
  return `S${seasonNumber} E${episodeNumber}`;
}

/**
 * Returns a label containing voucher duration information.
 * @param {Number} duration - Voucher duration.
 * @param {Boolean} needsSubscriptionLabel - Value that determines the subscription label to be used.
 * @returns {String} Voucher duration label.
 * @public
 */
export function getVoucherDurationText(duration, needsSubscriptionLabel = false) {
  return `${i18n.get(`global.eVoucher.duration.${!!needsSubscriptionLabel ? "subscription" : "period"}.${duration}m`)}`;
}
const getHasFrenchAudio = (title) => {
    if(title?.hasFrenchAudio) return true;
    
    if(title?.languages === null || !title?.languages?.length) return false;
    let language = title?.languages?.find(lang => i18n.get(`global.languages.${lang}`) || lang  === 'fr' || i18n.get(`global.languages.${lang}`) || lang  === "French");
    
    return language === 'fr' || language === "French" ? true : false;
  }